/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import styled from 'styled-components';
import ChartLabel from 'components/Label/ChartLabel';
import { usePercentagesFromArray } from 'hooks/usePercentagesFromArray';
import useHorizontalBarLabel from 'hooks/useHorizontalBarLabel';
import useHorizontalBarData from 'hooks/useHorizontalBarData';
import useAddCenterDataInDoughnut from 'hooks/useAddCenterDataInDoughnut';
import { color } from 'asserts/styles/colors';
import { commonTooltipOptions } from 'consts/chartOptions';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
  BarElement
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import useAddBiggerDoughnut from 'hooks/useAddBiggerDoughnut';
import uuid from 'react-uuid';
import { useAtomValue } from 'jotai';
import productAtom from 'atom/productAtom';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title
);

interface CommonModalChartProps {
  labels: string[];
  dataTypes: any;
  data: any;
  isTwoLabel?: boolean;
  activeTab: number;
  directions?: string[];
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
  setRightLabel?: React.Dispatch<React.SetStateAction<boolean>>;
  isTwoChart: boolean;
}

const CommonModalChart = ({
  labels,
  dataTypes,
  data,
  activeTab,
  directions,
  setActiveTab,
  setRightLabel,
  isTwoChart
}: CommonModalChartProps) => {
  const reverseLabel = [...labels].reverse();
  const twoDataLabel = [...reverseLabel, ...labels];

  const colors = data.datasets[0].backgroundColor;
  const LabelsData = data.labels;
  const countsData = data.datasets[0].data;

  const getPercentagesFromArray = usePercentagesFromArray();

  const percentCountData = getPercentagesFromArray(countsData);

  const [activeUiTab, setActiveUiTab] = useState(1);

  const productNumber = useAtomValue(productAtom);

  const onClickTab = (index: number) => {
    if (isTwoChart) {
      setActiveUiTab(index);

      switch (index) {
        case 1:
          setRightLabel && setRightLabel(false);
          setActiveTab(0);
          break;
        case 2:
          setRightLabel && setRightLabel(true);
          setActiveTab(0);
          break;
        case 3:
          setRightLabel && setRightLabel(true);
          setActiveTab(1);
          break;

        default:
          setRightLabel && setRightLabel(false);
          setActiveTab(1);
          break;
      }
    } else {
      setActiveTab(index);
    }
  };

  const options = {
    layout: {
      padding: 20
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: commonTooltipOptions
    },
    activeTab,
    borderWidth: 0
  };

  const centerData = {
    id: 'centerData',
    beforeDatasetsDraw: useAddCenterDataInDoughnut()
  };

  const addBiggerChart = {
    id: 'addBiggerChart',
    beforeDatasetsDraw: useAddBiggerDoughnut()
  };

  // rom 분석에는 적용 안되도록
  const addBiggerChartPlugin = labels.length >= 3 ? [addBiggerChart] : [];

  const sumData = countsData.reduce(
    (accumulator: number, currentValue: number) => {
      return accumulator + currentValue;
    },
    0
  );

  const barOptions = {
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      legend: {
        display: false
      },
      tooltip: commonTooltipOptions
    },
    scales: {
      x: {
        display: false,
        reverse: true,
        max: sumData,
        afterDataLimits: (scale: any) => {
          scale.max = scale.max * 1.4;
        }
      },
      y: {
        display: false,
        grid: {
          display: false
        },
        ticks: {
          dislay: false,
          font: {
            size: 13,
            weight: 'bold',
            family: 'Noto Sans KR'
          }
        }
      }
    }
  };

  const addDirectionInLabels = labels.map((level, index) => {
    if (level === '정상') {
      return level;
    } else {
      const location =
        index < 3 ? directions && directions[0] : directions && directions[1];
      return `${level} (${location})`;
    }
  });

  return (
    <>
      <Wrapper>
        <ChartWrapper $isTwoChart={isTwoChart}>
          {isTwoChart ? (
            <>
              {twoDataLabel.map((i, index) => (
                <Tabs
                  key={uuid()}
                  className={index === activeUiTab ? 'tab focused' : 'tab'}
                  onClick={() => onClickTab(index)}
                >
                  {i}
                </Tabs>
              ))}
            </>
          ) : (
            <>
              {labels.map((i, index) => (
                <Tabs
                  key={index}
                  className={index === activeTab ? 'tab focused' : 'tab'}
                  onClick={() => onClickTab(index)}
                >
                  {i}
                </Tabs>
              ))}
            </>
          )}
        </ChartWrapper>
        <DoughnutDataWrapper>
          <DoughnutWrapper>
            <Doughnut
              data={data}
              options={{ ...options }}
              plugins={[centerData, ...addBiggerChartPlugin]}
            />
          </DoughnutWrapper>
          <RightDataWrapper>
            <LabelWrapper>
              {directions ? (
                <>
                  {addDirectionInLabels.map((label: string, index: number) => (
                    <ChartLabel
                      key={`${index}${label}`}
                      pointColor={colors[index]}
                      text={label}
                    />
                  ))}
                </>
              ) : (
                <>
                  {LabelsData.map((label: string, index: number) => (
                    <ChartLabel
                      key={`${index}${label}`}
                      pointColor={colors[index]}
                      text={label}
                    />
                  ))}
                </>
              )}
            </LabelWrapper>
            <DataWrapper>
              <div>
                {countsData.map((number: number, index: number) => (
                  <div
                    key={index}
                    className={index === activeTab ? 'tab focused' : 'tab'}
                  >
                    {number.toLocaleString()}명 /
                  </div>
                ))}
              </div>
              &nbsp;
              <div>
                {percentCountData.map((number: number, index: number) => (
                  <div
                    key={index}
                    className={index === activeTab ? 'tab focused' : 'tab'}
                  >
                    {number.toFixed(1)}%
                  </div>
                ))}
              </div>
            </DataWrapper>
          </RightDataWrapper>
        </DoughnutDataWrapper>
        <CommonChartWrapper $productNumber={productNumber}>
          {dataTypes.map((posture: any, index: number) => (
            <CommonChart
              data={posture.data}
              key={index}
              title={posture.title}
              labels={posture.label}
              color={posture.color}
              options={barOptions}
            />
          ))}
        </CommonChartWrapper>
      </Wrapper>
    </>
  );
};

export default CommonModalChart;

const CommonChart = ({
  title,
  color,
  options,
  labels,
  data: chartData
}: any) => {
  const data = {
    labels,
    datasets: [
      {
        label: `${title}Data`,
        data: chartData,
        borderColor: color,
        backgroundColor: color,
        borderWidth: 1,
        barThickness: 6,
        borderRadius: 3
      }
    ]
  };

  const addDataPlugins = {
    id: 'addDataPlugins',
    afterDatasetsDraw: useHorizontalBarData()
  };

  const addLabelsData = {
    id: 'addLabelsData',
    beforeDatasetsDraw: useHorizontalBarLabel()
  };

  return (
    <>
      <ChartTitle>{title}</ChartTitle>
      <BarWrapper>
        <Bar
          data={data}
          options={options}
          plugins={[addDataPlugins, addLabelsData]}
        />
      </BarWrapper>
    </>
  );
};

const ChartWrapper = styled.div<{
  $isTwoChart: boolean;
}>`
  position: relative;
  display: flex;
  justify-content: center;
  gap: 16px;

  & div:nth-of-type(2) {
    margin-right: ${(props) => (props.$isTwoChart ? '16px' : 0)};
  }

  & div:nth-child(3)::before {
    content: '';
    position: absolute;
    top: 0;
    right: 50%;
    transform: translateX(50%);
    width: ${(props) => (props.$isTwoChart ? '1px' : 0)};
    height: 80%;
    background-color: ${color.lightopacity80};
  }
`;

const Tabs = styled.div`
  padding-bottom: 4px;
  font-size: 14px;
  cursor: pointer;
  color: ${color.black06};
  font-weight: 500;

  &.focused {
    color: ${color.black};
    border-bottom: 2px solid ${color.black};
  }
`;

const Wrapper = styled.div``;
const CommonChartWrapper = styled.div<{ $productNumber: number }>`
  & div:nth-of-type(2) {
    height: ${(props) =>
      props.$productNumber === 9
        ? '79px'
        : props.$productNumber === 8
          ? '119px'
          : '118px'};
  }
`;

const BarWrapper = styled.div`
  &:first-of-type {
    height: 118px;
  }
  height: 136px;

  &:last-of-type {
    height: 49px;
  }

  margin: 4px auto 16px;
`;

const DoughnutWrapper = styled.div`
  width: 170px;
`;

const DoughnutDataWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  line-height: 20px;
  text-align: right;
`;

const RightDataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const LabelWrapper = styled.div``;

const DataWrapper = styled.div`
  display: flex;
  font-size: 13px;
  color: ${color.black04};

  & div.focused {
    color: ${color.black};
    font-weight: bold;
  }
`;

const ChartTitle = styled.div`
  padding-bottom: 8px;
  font-weight: 500;
  font-size: 16px;
  line-height: 18px;
  border-bottom: 1px solid ${color.lightopacity30};
`;
